import { zodResolver } from "@hookform/resolvers/zod";
import { usePersonalInformationValidation } from "Components/Onboarding/shared/Hooks/usePersonalInformationValidation";
import { EditValues } from "Components/Onboarding/shared/PersonalInformation/EditValues";
import { ViewValues } from "Components/Onboarding/shared/PersonalInformation/ViewValues";
import {
  getPersonalInformationValidationSchema,
  type PersonalInformationConfirmationFormModel,
} from "Components/Onboarding/shared/PersonalInformation/schema";
import { StepBox } from "Components/Onboarding/shared/StepBox";
import {
  getPartyWithSingleAddress,
  upsertAddressByType,
} from "Components/Onboarding/utils";

import { FullHeightForm } from "Components/Shared/FormStyles";
import { CheckboxInput } from "Components/Shared/Inputs/Form/CheckboxInput";
import {
  AddressType,
  CountryCode,
  type AddressDto,
  type PartyDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { type JSX, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

const StepResources = Resources.Onboarding.Steps.PersonalInformation;

type Props = {
  initialValues: PartyDto;
  onSave: (values: PartyDto) => void;
  /** Called with the confirmation form's values once the party data passed validation. */
  onNextStepRequested: (
    values: PersonalInformationConfirmationFormModel,
  ) => void;
  companyHeadquartersAddress?: AddressDto;
  /** When given, the Stardust consent checkbox (ZS-1624) is a field of this form, pre-checked,
      rendered with this label under the data confirmation. The customer flow passes it.
      JSX.Element (not ReactNode) because that is what CheckboxInput.label accepts. */
  stardustConsentLabel?: JSX.Element;
  /** The Díky Tobě consent checkbox (ZS-1623), the same way: pre-checked (every user is in the
      program unless they decline), rendered with this label. The customer flow passes it. */
  thanksToYouConsentLabel?: JSX.Element;
};

export const PersonalInformation = ({
  initialValues: initialValuesFromProps,
  onSave,
  onNextStepRequested,
  companyHeadquartersAddress,
  stardustConsentLabel,
  thanksToYouConsentLabel,
}: Props) => {
  const [isDataInvalid, setIsDataInvalid] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [savedValues, setSavedValues] = useState<PartyDto | null>(null);

  const { t } = useResource();
  const userDetailValidationSchema = usePersonalInformationValidation();
  const personalInformationValidationSchema =
    getPersonalInformationValidationSchema(t);

  const currentValues = savedValues ?? initialValuesFromProps;
  const initialValues = {
    ...currentValues,
    nationality: currentValues.nationality ?? CountryCode.CZ, // If there's none, default to the home market - Czechia
  };
  const { partyIdentification } = initialValues;

  const isAllIDDataScanned = !!(
    partyIdentification?.identification &&
    partyIdentification?.identificationNumber &&
    partyIdentification?.identificationValidFrom &&
    partyIdentification?.identificationValidTo
  );

  const isStardustConsentVisible = stardustConsentLabel !== undefined;
  const isThanksToYouConsentVisible = thanksToYouConsentLabel !== undefined;
  const defaultValues: Partial<PersonalInformationConfirmationFormModel> = {
    isIDCardApprovedByClient:
      partyIdentification?.isIDCardApprovedByClient == null //has it not been answered yet? (loose equality == on purpose here)
        ? isAllIDDataScanned
          ? undefined //everything is scanned, let the user decide if it's correct or not
          : false //something is missing, so the scan is implicitly incorrect and we don't even have to ask the user
        : partyIdentification?.isIDCardApprovedByClient,
    isStardustParticipant: isStardustConsentVisible || undefined,
    isThanksToYouParticipant: isThanksToYouConsentVisible || undefined,
  };
  const { control, handleSubmit, watch } =
    useForm<PersonalInformationConfirmationFormModel>({
      resolver: zodResolver(personalInformationValidationSchema),
      defaultValues,
    });
  const isUserConfirmed = watch("isUserConfirmed");
  const isIDCardApprovedByClient = watch("isIDCardApprovedByClient");

  const handleSave = (values: PartyDto) => {
    const mergedValues = {
      ...initialValues,
      ...values,
      partyIdentification: {
        ...values.partyIdentification,
        isIDCardApprovedByClient,
      },
    };
    onSave(mergedValues);
    setSavedValues(mergedValues);
    setIsEdit(false);
  };

  const onSubmit = async (
    formValues: PersonalInformationConfirmationFormModel,
  ) => {
    const party = {
      ...getPartyWithSingleAddress(initialValues, AddressType.Permanent),
      // Form-only field the schema requires; derived like the edit form's defaults (utils.ts).
      hasOtherNationality: initialValues.otherNationalities.length > 0,
      partyIdentification: {
        ...partyIdentification,
        isIDCardApprovedByClient,
      },
    };

    const { success, data } = userDetailValidationSchema.safeParse(party);

    if (success) {
      const { hasOtherNationality: _formOnly, ...validParty } = data; // never part of PartyDto
      onSave({
        ...initialValues,
        ...validParty,
        addresses: upsertAddressByType(
          initialValues.addresses || [],
          validParty.address,
        ),
        licenses: initialValues.licenses || [],
      });
      onNextStepRequested(formValues);
      return;
    }

    setIsEdit(true);
    setIsDataInvalid(true);
    toast.error(t(Resources.PersonalInfo.FillMissingData));
  };

  if (isEdit) {
    return (
      <EditValues
        onSave={handleSave}
        initialValues={initialValues}
        shouldValidateOnMount={isDataInvalid}
        companyHeadquartersAddress={companyHeadquartersAddress}
      />
    );
  }

  return (
    <FullHeightForm onSubmit={handleSubmit(onSubmit)}>
      <StepBox
        title={t(StepResources.Title)}
        submitButtonLabel={t(Resources.Common.Continue)}
        submitDisabled={!isUserConfirmed}
        withDivider
      >
        <ViewValues
          userDetail={initialValues}
          control={control}
          onEdit={() => {
            setIsEdit(true);
          }}
          isIDScanConfirmationVisible={isAllIDDataScanned}
          isIDScanMarkedAsIncorrect={isIDCardApprovedByClient === false} //we must check for false explicitly here, it could be null too
        />
        <CheckboxInput
          label={t(StepResources.DataConfirmation)}
          control={control}
          name="isUserConfirmed"
        />
        {isStardustConsentVisible && (
          <CheckboxInput
            label={stardustConsentLabel}
            control={control}
            name="isStardustParticipant"
          />
        )}
        {isThanksToYouConsentVisible && (
          <CheckboxInput
            label={thanksToYouConsentLabel}
            control={control}
            name="isThanksToYouParticipant"
          />
        )}
      </StepBox>
    </FullHeightForm>
  );
};
